<template>
    <div class="ee-page">
        <!-- Header -->
        <div class="ee-header">
            <div class="ee-header-left">
                <router-link to="/employees" class="ee-back-btn"><i class="fas fa-arrow-left"></i></router-link>
                <div>
                    <h1 class="ee-title">{{ editMode ? (firstName ? `${firstName} ${lastName}` : 'Update Employee') : 'Create Employee' }}</h1>
                    <p class="ee-subtitle" v-if="editMode">Edit employee profile, documents, and payroll</p>
                    <p class="ee-subtitle" v-else>Add a new employee to the system</p>
                </div>
            </div>
            <div class="ee-header-actions" v-if="editEmployeeData">
                <button class="ee-hbtn ee-hbtn-email" @click="sendWelcomeEmail" :disabled="sendingWelcomeEmail">
                    <i class="fas fa-envelope"></i>
                    {{ sendingWelcomeEmail ? 'Sending...' : editEmployeeData.isWelcomeEmailSent ? 'Resend Email' : 'Welcome Email' }}
                </button>
                <button class="ee-hbtn ee-hbtn-dark" @click="showIncrementModal = true">
                    <i class="fas fa-arrow-up"></i> Increment
                </button>
                <button class="ee-hbtn" :class="editEmployeeData.status ? 'ee-hbtn-danger' : 'ee-hbtn-success'" @click="updateUserStatus(editEmployeeData.status ? 0 : 1)" :disabled="updatingStatus">
                    <i :class="editEmployeeData.status ? 'fas fa-user-slash' : 'fas fa-user-check'"></i>
                    {{ updatingStatus ? 'Please wait...' : editEmployeeData.status ? 'Deactivate' : 'Activate' }}
                </button>
            </div>
        </div>

        <!-- Status alerts -->
        <div class="ee-alert ee-alert-warn" v-if="editEmployeeData && !editEmployeeData.status && editEmployeeData.lastDeactivatedStatusDate">
            <i class="fas fa-info-circle"></i> User last deactivated on {{ new Date(editEmployeeData.lastDeactivatedStatusDate).toLocaleString() }}
        </div>
        <div class="ee-alert ee-alert-danger" v-if="!companyId">
            <i class="fas fa-exclamation-triangle"></i> Company details not filled
        </div>
        <div class="ee-alert ee-alert-danger" v-if="!documentDetailsCompleted">
            <i class="fas fa-exclamation-triangle"></i> Document details not completed
        </div>

        <!-- Increment Modal -->
        <CustomModal v-if="showIncrementModal" :title="`Increment Salary`" :show="showIncrementModal" @close="showIncrementModal = false">
            <div class="ee-modal-body">
                <p class="ee-modal-label">Enter the percentage to increment:</p>
                <custom-input v-model="incrementPercent" type="number" label="Increment Percentage*" />
                <button class="ee-btn ee-btn-primary" style="margin-top:10px" @click="moveToIncrementSalary">Next</button>
            </div>
        </CustomModal>

        <!-- Tabs -->
        <div class="ee-tabs">
            <button
                v-for="tab of Object.values(ACTIVE_TABS)"
                :key="tab.name"
                :class="['ee-tab', { 'ee-tab-active': activeTab.name === tab.name }]"
                @click="handleTabChange(tab)"
            >
                <i :class="tabIcon(tab.name)" class="ee-tab-icon"></i>
                {{ tab.value }}
            </button>
        </div>

        <div v-if="!editMode || editEmployeeData">

            <!-- ===== PERSONAL TAB ===== -->
            <div v-show="activeTab.value == ACTIVE_TABS.PERSONAL.value">
                <!-- Login Details -->
                <div class="ee-section">
                    <div class="ee-section-head"><i class="fas fa-key ee-section-icon"></i><span>Login Details</span></div>
                    <div class="ee-grid">
                        <div class="ee-field">
                            <custom-input v-model="username" required label="Username" />
                        </div>
                        <div class="ee-field">
                            <PasswordInputVue v-model="password" :editMode="editMode" :required="!editMode" />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="empCode" type="number" required label="Employee Code" />
                        </div>
                        <div class="ee-field">
                            <SelectDropdown
                                :id="`user-role`"
                                :options="userRoles"
                                :multiple="false"
                                :field="'roleId'"
                                :value="roleId"
                                @change="roleId = $event"
                                title="User Role*"
                            />
                        </div>
                    </div>
                </div>

                <!-- Face Auth (edit mode) -->
                <div class="ee-section" v-if="editMode">
                    <div class="ee-section-head"><i class="fas fa-camera ee-section-icon"></i><span>Face Authentication</span></div>
                    <div v-if="!authImage1" class="ee-empty-sm">
                        <i class="fas fa-image"></i> User has not uploaded from app
                    </div>
                    <div v-else>
                        <div class="ee-alert ee-alert-danger" v-if="!authImage1Verified" style="margin-bottom:12px">
                            <i class="fas fa-exclamation-circle"></i> Verification Pending!
                        </div>
                        <div class="ee-face-auth">
                            <img class="ee-face-img" :src="serverURL + authImage1" />
                            <div class="ee-face-actions">
                                <button class="ee-btn ee-btn-outline" @click="rotateAuthImage">{{ rotating ? 'Please wait...' : 'Rotate Right' }}</button>
                                <button class="ee-btn ee-btn-outline" @click="rotateAuthImage(true)">{{ flipping ? 'Please wait...' : 'Flip Horizontal' }}</button>
                                <button class="ee-btn ee-btn-outline ee-btn-red" @click="removeAuthImage">{{ removingFaceAuth ? 'Please wait...' : 'Remove Face Auth' }}</button>
                                <button v-if="!authImage1Verified" class="ee-btn ee-btn-primary" style="background:#ea580c" @click="approveAuthImage">{{ approvingFaceAuth ? 'Please wait...' : 'Approve Face Auth' }}</button>
                                <div v-else class="ee-face-toggle">
                                    <label class="ee-switch" :for="`form-authimage1`">
                                        <input class="form-check-input" type="checkbox" role="switch" :checked="authImage1Enabled" :id="`form-authimage1`" @change="updateAuthImage1Enabled" :disabled="updatingAuthImage1Enabled" />
                                        <span>{{ authImage1Enabled ? 'Disable' : 'Enable' }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Personal Details -->
                <div class="ee-section">
                    <div class="ee-section-head"><i class="fas fa-user ee-section-icon"></i><span>Personal Details</span></div>
                    <div class="ee-grid">
                        <div class="ee-field">
                            <SelectDropdown
                                :id="`form-salutation`"
                                :options="[
                                    { id: 'Sir', title: 'Sir' },
                                    { id: 'Madam', title: 'Madam' },
                                    { id: 'Mr', title: 'Mr' },
                                    { id: 'Ms', title: 'Ms' },
                                    { id: 'Miss', title: 'Miss' },
                                    { id: 'Mrs', title: 'Mrs' },
                                ]"
                                :multiple="false"
                                :field="'salutation'"
                                :value="salutation"
                                @change="salutation = $event"
                                title="Salutation"
                            />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="firstName" required label="First Name" />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="lastName" required label="Last Name" />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="fatherName" required label="Father's Name" />
                        </div>
                        <div class="ee-field">
                            <custom-input type="date" label="Date of Birth" v-model="dob" />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="email" required type="email" label="Email" />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="phone" type="number" required label="Phone Number (10 digits)" />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="joiningDate" type="date" label="Joining Date" />
                        </div>
                        <div class="ee-field">
                            <OldImageView :src="profileImageOld" />
                            <custom-input v-model="profileImage" type="file" label="Profile Image" />
                        </div>
                        <div class="ee-field-full">
                            <CountryStateCitySelector
                                v-if="!editMode || (editMode && editEmployeeData)"
                                :countryId="country"
                                :stateId="state"
                                :cityId="city"
                                @country="handleCountryUpdate"
                                @state="state = $event"
                                @city="city = $event"
                            />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="address" label="Address*" />
                        </div>
                    </div>
                </div>

                <!-- Emergency Contact -->
                <div class="ee-section">
                    <div class="ee-section-head"><i class="fas fa-phone-alt ee-section-icon"></i><span>Emergency Contact</span></div>
                    <div class="ee-grid">
                        <div class="ee-field">
                            <custom-input v-model="emergencyContactName" required label="Emergency Contact Name" />
                        </div>
                        <div class="ee-field">
                            <custom-input required type="email" v-model="emergencyContactEmail" label="Emergency Contact Email" />
                        </div>
                        <div class="ee-field">
                            <custom-input type="number" required v-model="emergencyContactPhone" label="Emergency Contact Phone" />
                        </div>
                    </div>
                </div>

                <!-- Company Details -->
                <div class="ee-section">
                    <div class="ee-section-head"><i class="fas fa-building ee-section-icon"></i><span>Company Details</span></div>
                    <div class="ee-grid">
                        <div class="ee-field">
                            <label class="ee-label">Salary Company*</label>
                            <SelectDropdown
                                :id="`form-companyId`"
                                :options="companyMasters"
                                :multiple="false"
                                :field="'companyId'"
                                :value="companyId"
                                @change="companyId = $event"
                            />
                        </div>
                        <div class="ee-field">
                            <label class="ee-label">Related Companies</label>
                            <SelectDropdown
                                :id="`form-relatedCompanyId`"
                                :options="companyMasters"
                                :multiple="true"
                                :field="'relatedCompanyIds'"
                                :value="relatedCompanyIds"
                                @change="relatedCompanyIds = $event"
                            />
                        </div>
                        <div class="ee-field">
                            <SelectDropdown
                                :id="`supervisor_user`"
                                :field="`supervisor_user`"
                                v-if="employees.length > 0"
                                :options="employees.map((item) => ({ id: item.id, title: `${item.firstName || ''} ${item.lastName || ''}` }))"
                                :multiple="false"
                                title="Supervisor/Reporter"
                                :value="supervisorUserId"
                                @change="supervisorUserId = $event"
                            />
                        </div>
                        <div class="ee-field">
                            <label class="ee-label">Department*</label>
                            <SelectDropdown
                                :id="`form-departmentId`"
                                :options="departmentMasters"
                                :multiple="false"
                                field="form-departmentId"
                                :value="departmentId"
                                @change="departmentId = $event"
                            />
                        </div>
                        <div class="ee-field">
                            <label class="ee-label">Designation*</label>
                            <SelectDropdown
                                :id="`form-designationId`"
                                :options="designationMasters"
                                :multiple="false"
                                field="form-designationId"
                                :value="designationId"
                                @change="designationId = $event"
                            />
                        </div>
                        <div class="ee-field">
                            <label class="ee-label">Employment Status*</label>
                            <SelectDropdown
                                :id="`form-employment-status`"
                                :options="employmentStatusMasters"
                                :multiple="false"
                                field="form-employment-status"
                                :value="employmentStatusId"
                                @change="employmentStatusId = $event"
                            />
                        </div>
                        <div class="ee-field">
                            <label class="ee-label">Work Location*</label>
                            <SelectDropdown
                                v-if="mastersInitialized"
                                :id="`form-work-location`"
                                :options="filteredWorkLocations"
                                :multiple="false"
                                field="form-work-location"
                                :value="workLocationId"
                                @change="workLocationId = $event"
                            />
                        </div>
                    </div>
                </div>

                <!-- Personal Tab Actions -->
                <div class="ee-actions">
                    <button class="ee-btn ee-btn-outline" @click="cancelEdit"><i class="fas fa-times"></i> Cancel</button>
                    <button class="ee-btn ee-btn-outline" @click="handleTabChange(ACTIVE_TABS.WORK)"><i class="fas fa-arrow-right"></i> Next: Documents</button>
                    <button class="ee-btn ee-btn-primary" @click="createOrUpdateEmployee" :disabled="processing">
                        <i :class="processing ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                        {{ processing ? 'Saving...' : editMode ? 'Update Employee' : 'Create Employee' }}
                    </button>
                </div>
            </div>

            <!-- ===== WORK / DOCS TAB ===== -->
            <div v-show="activeTab.value == ACTIVE_TABS.WORK.value">
                <!-- Identity Documents -->
                <div class="ee-section">
                    <div class="ee-section-head"><i class="fas fa-id-card ee-section-icon"></i><span>Identity Documents</span></div>
                    <div class="ee-doc-grid">
                        <div class="ee-doc-row">
                            <div class="ee-doc-label"><i class="fas fa-fingerprint"></i> Aadhaar Card</div>
                            <div class="ee-doc-fields">
                                <custom-input v-model="aadhaarCardId" required label="Aadhaar Number" />
                            </div>
                            <div class="ee-doc-fields">
                                <custom-input type="file" v-model="aadhaarCardUrl" label="Upload PDF/Image" required />
                                <OldImageView linkMode :src="aadhaarCardUrlOld" />
                            </div>
                        </div>
                        <div class="ee-doc-row">
                            <div class="ee-doc-label"><i class="fas fa-address-card"></i> PAN Card</div>
                            <div class="ee-doc-fields">
                                <custom-input v-model="panCardId" label="PAN Number" />
                            </div>
                            <div class="ee-doc-fields">
                                <custom-input type="file" v-model="panCardUrl" label="Upload PDF/Image" />
                                <OldImageView linkMode :src="panCardUrlOld" />
                            </div>
                        </div>
                        <div class="ee-doc-row">
                            <div class="ee-doc-label"><i class="fas fa-passport"></i> Passport</div>
                            <div class="ee-doc-fields">
                                <custom-input v-model="passportId" label="Passport Number" />
                            </div>
                            <div class="ee-doc-fields">
                                <custom-input type="file" v-model="passportUrl" label="Upload PDF/Image" />
                                <OldImageView linkMode :src="passportUrlOld" />
                            </div>
                        </div>
                        <div class="ee-doc-row">
                            <div class="ee-doc-label"><i class="fas fa-car"></i> Driving License</div>
                            <div class="ee-doc-fields">
                                <custom-input v-model="drivingLicenseId" label="License ID" />
                            </div>
                            <div class="ee-doc-fields">
                                <custom-input type="file" v-model="drivingLicenseUrl" label="Upload PDF/Image" />
                                <OldImageView linkMode :src="drivingLicenseUrlOld" />
                            </div>
                        </div>
                        <div class="ee-doc-row">
                            <div class="ee-doc-label"><i class="fas fa-file-alt"></i> Local ID Proof</div>
                            <div class="ee-doc-fields">
                                <custom-input v-model="localIdProofId" label="Local Proof ID" />
                            </div>
                            <div class="ee-doc-fields">
                                <custom-input type="file" v-model="localIdProofUrl" label="Upload PDF/Image" />
                                <OldImageView linkMode :src="localIdProofUrlOld" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Bank Details -->
                <div class="ee-section">
                    <div class="ee-section-head"><i class="fas fa-university ee-section-icon"></i><span>Bank Account Details</span></div>
                    <div class="ee-grid">
                        <div class="ee-field">
                            <label class="ee-label">Bank Name</label>
                            <SelectDropdown
                                v-if="bankMasters.length"
                                :id="`bankMasterId`"
                                :options="bankMasters.map((item) => ({ id: item.id, title: item.name }))"
                                field="bankMasterId"
                                :value="bankMasterId"
                                @change="bankMasterId = $event"
                            />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="bankAccountName" label="Bank Account Name" />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="bankAccountNumber" label="Bank Account Number" />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="bankAccountBranch" label="Bank Branch Name" />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="bankAccountIfsc" label="Bank IFSC Code" />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="bankAccountSwiftId" label="Bank Swift Code" />
                        </div>
                        <div class="ee-field">
                            <custom-input v-model="uanNumber" type="text" label="UAN Number" />
                        </div>
                    </div>
                </div>

                <DocumentGeneration v-if="editEmployeeData" :employee="editEmployeeData" />

                <!-- Work Tab Actions -->
                <div class="ee-actions">
                    <button class="ee-btn ee-btn-primary" @click="createOrUpdateEmployee" :disabled="processing">
                        <i :class="processing ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                        {{ processing ? 'Saving...' : editMode ? 'Update Employee' : 'Create Employee' }}
                    </button>
                </div>
            </div>

            <!-- ===== ATTENDANCE TAB ===== -->
            <div v-show="activeTab.value == ACTIVE_TABS.ATTENDANCE.value" v-if="$route.params.id">
                <employee-attendance-management :employeeId="$route.params.id" />
            </div>

            <!-- ===== NOTICE PERIOD / ASSETS TAB ===== -->
            <div v-if="$route.params.id && activeTab.value == ACTIVE_TABS.NOTICE_PERIOD.value">
                <EmployeeAssetManager :employeeId="$route.params.id" />
                <NoticePeriod :empId="$route.params.id" />
            </div>

            <!-- ===== CTC HISTORY TAB ===== -->
            <div v-if="$route.params.id && activeTab.value == ACTIVE_TABS.CTC_HISTORY.value">
                <IndexTemplate :employeeId="$route.params.id" />
            </div>

            <!-- ===== PAYROLL TAB ===== -->
            <div v-if="$route.params.id && activeTab.value == ACTIVE_TABS.PAYROLL.value">
                <PayheadValuesEditForm :editEmployeeData="editEmployeeData" :employeeId="$route.params.id" />
            </div>
        </div>
        <div v-else class="ee-loading">
            <i class="fas fa-circle-notch fa-spin"></i>
            <span>Loading employee data...</span>
        </div>
    </div>
</template>

<script>
import { http, serverURL } from "@/utils/http"
import { createField, createColumn } from "@/utils/common"
import CustomInput from "../../components/crud/custom-input.vue"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import OldImageView from "../../components/UI/OldImageView.vue"
import CountryStateCitySelector from "../../components/UI/CountryStateCitySelector.vue"
import EmployeeAttendanceManagement from "./employee-attendance-management"
import PayheadValuesEditForm from "../../components/payroll/PayheadValuesEditForm.vue"
import NoticePeriod from "../../components/employee/NoticePeriod.vue"
import CustomModal from "../../components/layout/CustomModal.vue"
import PasswordInputVue from "../../components/crud/password-input.vue"
import { errMsg, getPasswordError, onlyAlphabetic } from "../../utils/common"
import IndexTemplate from "../salary-update-histories/indexTemplate.vue"
import DocumentGeneration from "../../components/employee/DocumentGeneration.vue"
import EmployeeAssetManager from "../../components/employee/EmployeeAssetManager.vue"
import { mapState } from "vuex"

const ACTIVE_TABS = {
    PERSONAL: { value: "Personal Details", name: "PERSONAL" },
    WORK: { value: "Docs", name: "WORK" },
    ATTENDANCE: { value: "Attendance", name: "ATTENDANCE" },
    NOTICE_PERIOD: { value: "Notice Period/Assets", name: "NOTICE_PERIOD" },
    CTC_HISTORY: { value: "CTC History", name: "CTC_HISTORY" },
    PAYROLL: { value: "Payroll", name: "PAYROLL" },
}

export default {
    data() {
        return {
            updatingAuthImage1Enabled: false,
            supervisorUserId: "",
            userInitialized: false,
            flipping: false,
            rotating: false,
            approvingFaceAuth: false,
            removingFaceAuth: false,
            incrementPercent: 10,
            serverURL,
            activeTab: ACTIVE_TABS.PERSONAL,
            processing: false,
            mastersInitialized: false,
            loading: true,
            username: "",
            authImage1: "",
            authImage1Verified: "",
            authImage1Enabled: false,
            empCode: "",
            password: "",
            fatherName: "",
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            joiningDate: "",
            country: "",
            profileImage: "",
            city: "",
            state: "",
            address: "",
            emergencyContactName: "",
            emergencyContactEmail: "",
            emergencyContactPhone: "",
            uanNumber: "",
            dob: "",
            // master ids
            companyId: "",
            departmentId: "",
            designationId: "",
            employmentStatusId: "",
            workLocationId: "",
            // documents
            aadhaarCardId: "",
            aadhaarCardUrl: "",
            panCardId: "",
            panCardUrl: "",
            passportId: "",
            passportUrl: "",
            drivingLicenseId: "",
            drivingLicenseUrl: "",
            localIdProofId: "",
            localIdProofUrl: "",
            // back account name
            bankMasterId: "",
            bankAccountName: "",
            bankAccountNumber: "",
            bankAccountBankName: "",
            bankAccountBranch: "",
            bankAccountIfsc: "",
            bankAccountSwiftId: "",
            // employee creation masters...
            companyMasters: [],
            companyMastersFull: [],
            departmentMasters: [],
            designationMasters: [],
            employmentStatusMasters: [],
            workLocationMasters: [],
            // edit related fields
            editMode: true,
            roleId: "",
            userRoles: [],
            relatedCompanyIds: [],
            editEmployeeData: null,
            profileImageOld: "",
            aadhaarCardUrlOld: "",
            panCardUrlOld: "",
            passportUrlOld: "",
            drivingLicenseUrlOld: "",
            localIdProofUrlOld: "",
            updatingStatus: false,
            showIncrementModal: false,
            ACTIVE_TABS,
            sendingWelcomeEmail: false,
            employees: [],
        }
    },
    props: { videoId: { type: String, default: "" } },
    components: {
        DocumentGeneration,
        EmployeeAttendanceManagement,
        CountryStateCitySelector,
        OldImageView,
        CustomInput,
        SelectDropdown,
        PayheadValuesEditForm,
        NoticePeriod,
        CustomModal,
        PasswordInputVue,
        IndexTemplate,
        EmployeeAssetManager,
    },
    async mounted() {
        const tab = this.$route.query.active_tab
        if (tab) {
            const found = Object.values(ACTIVE_TABS).find((item) => item.name === tab)
            if (found) this.activeTab = found
        }
        await this.fetchEmployeeCreationMasters()
        await this.initIfEditMode()
        await this.fetchUsersForGroupCreation()
    },

    computed: {
        ...mapState("misc", ["countries", "bankMasters"]),
        documentDetailsCompleted() {
            return !(!this.aadhaarCardId || (!this.editMode && !this.aadhaarCardUrl))
            return !(
                !this.aadhaarCardId ||
                !this.panCardId ||
                (!this.editMode && (!this.aadhaarCardUrl || !this.panCardUrl)) ||
                !this.bankAccountName ||
                !this.bankAccountNumber ||
                !this.bankAccountBankName ||
                !this.bankAccountBranch ||
                !this.bankAccountIfsc
            )
        },
        filteredWorkLocations() {
            const selectedCompanies = this.companyMastersFull.filter((item) => {
                if (String(item.id) === String(this.companyId) || (this.relatedCompanyIds && this.relatedCompanyIds.map(String).includes(String(item.id)))) {
                    return true
                }
                return false
            })

            const workLocationFiltered = this.workLocationMasters.filter((item) => {
                return selectedCompanies.find((company) => company.workLocationIds && company.workLocationIds.map(String).includes(String(item.id)))
            })
            if (!workLocationFiltered.find((item) => item.id == this.workLocationId)) {
                this.workLocationId = ""
            }
            return workLocationFiltered
        },
    },
    methods: {
        handleTabChange(tab) {
            this.activeTab = tab
            const url = this.$router.resolve({ ...this.$route, query: { ...this.$route.query, active_tab: tab.name } }).href
            this.$router.push(url)
        },
        tabIcon(name) {
            const icons = { PERSONAL: 'fas fa-user', WORK: 'fas fa-file-alt', ATTENDANCE: 'fas fa-clock', NOTICE_PERIOD: 'fas fa-bell', CTC_HISTORY: 'fas fa-chart-line', PAYROLL: 'fas fa-money-bill-wave' }
            return icons[name] || 'fas fa-circle'
        },
        async updateAuthImage1Enabled($event) {
            const enabled = $event.target.checked
            if (this.userInitialized) {
                try {
                    this.updatingAuthImage1Enabled = true
                    await http.post("/face-recognition/enable-disable", {
                        user_id: this.editEmployeeData.id,
                        enable: enabled,
                    })
                    this.$toast.success(`Image ${enabled ? "Enabled" : "Disabled"}!`)
                    window.location.href = this.$route.path + "?active_tab=PERSONAL"
                } catch (err) {
                    console.log("error in verifying auth image", err)
                }
                this.updatingAuthImage1Enabled = false
            }
        },
        async removeAuthImage() {
            try {
                await http.post("/face-recognition/remove", {
                    user_id: this.editEmployeeData.id,
                })
                this.$toast.success("Auth image removed")
                window.location.href = this.$route.path + "?active_tab=PERSONAL"
            } catch (err) {
                console.log("error in removing auth image", err)
            }
        },
        async approveAuthImage() {
            try {
                await http.post("/face-recognition/approve", {
                    user_id: this.editEmployeeData.id,
                })
                this.$toast.success("Image approved!")
                window.location.href = this.$route.path + "?active_tab=PERSONAL"
            } catch (err) {
                console.log("error in verifying auth image", err)
            }
        },
        async rotateAuthImage(flip = false) {
            try {
                if (this.rotating || this.flipping) return
                if (flip) {
                    this.flipping = true
                } else {
                    this.rotating = true
                }
                await http.post("/face-recognition/rotate", {
                    user_id: this.editEmployeeData.id,
                    flip,
                })
                window.location.href = this.$route.path + "?active_tab=PERSONAL"
                this.$toast.success("Image rotated")
            } catch (err) {
                console.log("error in fetching communication groups", err)
                this.$toast.error("Could not rotate image")
            }
            this.rotating = false
            this.flipping = false
        },
        async fetchUsersForGroupCreation() {
            try {
                const res = await http.get("/communication-groups/init")
                const resData = res.data.result
                this.employees = this.editEmployeeData
                    ? resData.employees.filter((item) => item.id != this.editEmployeeData.id)
                    : resData.employees
            } catch (err) {
                console.log("error in fetching communication groups", err)
            }
        },
        async sendWelcomeEmail() {
            if (!this.editEmployeeData.email) return this.$toast.error("Please update employee's email!")
            if (this.sendingWelcomeEmail) return
            try {
                this.sendingWelcomeEmail = true
                await http.post(`/auth/welcome-email/${this.editEmployeeData.id}`)
                this.editEmployeeData.isWelcomeEmailSent = true
                this.$toast.success("Welcome email sent! It can take 1-3 mins to receive!")
            } catch (err) {
                this.$toast.error("Could not send welcome email!")
            }
            this.sendingWelcomeEmail = false
        },
        moveToIncrementSalary() {
            if (this.incrementPercent >= 100) {
                return this.$toast.error("% Must be less than 100!")
            }
            window.location.href =
                this.$route.path + "?active_tab=" + ACTIVE_TABS.PAYROLL.name + "&increment_percent=" + this.incrementPercent
        },
        async updateUserStatus(statusToUpdate) {
            if (this.updatingStatus) return
            try {
                this.updatingStatus = true
                const res = await http.patch(`/users/${this.editEmployeeData.id}/update-status`, {
                    status: statusToUpdate,
                })
                const { status, lastDeactivatedStatusDate } = res.data.result
                this.editEmployeeData.status = status
                this.editEmployeeData.lastDeactivatedStatusDate = lastDeactivatedStatusDate
                this.$toast.success(status ? "User activated successfully" : "User Deactivated")
            } catch (err) {
                console.log("Could not update status", err)
                this.$toast.error(errMsg(err))
            }
            this.updatingStatus = false
        },
        handleCountryUpdate(update) {
            console.log("update country", update)
            this.country = update
        },
        cancelEdit() {
            this.$router.push("/employees")
        },
        async initIfEditMode() {
            const id = this.$route.params.id
            if (id) {
                this.editMode = true
                const res = await http.get("/users/" + id)
                this.editEmployeeData = res.data.result

                // set the defaults to each field.
                if (this.editEmployeeData) {
                    this.supervisorUserId = this.editEmployeeData.supervisorUserId || ""
                    this.salutation = this.editEmployeeData.salutation || ""
                    this.bankMasterId = this.editEmployeeData.bankMasterId || ""

                    this.roleId = this.editEmployeeData.roleId || ""
                    this.username = this.editEmployeeData.username || ""
                    this.empCode = this.editEmployeeData.empCode || ""
                    this.relatedCompanyIds = this.editEmployeeData.relatedCompanyIds || []
                    this.fatherName = this.editEmployeeData.fatherName || ""
                    this.firstName = this.editEmployeeData.firstName || ""
                    this.lastName = this.editEmployeeData.lastName || ""
                    this.authImage1 = this.editEmployeeData.authImage1 || ""
                    this.authImage1Verified = this.editEmployeeData.authImage1Verified || ""
                    this.authImage1Enabled = this.editEmployeeData.authImage1Enabled || false
                    this.email = this.editEmployeeData.email || ""
                    this.phone = this.editEmployeeData.phone || ""
                    this.joiningDate = this.editEmployeeData.joiningDate || ""
                    this.country = this.editEmployeeData.country || ""
                    this.city = this.editEmployeeData.city || ""
                    this.state = this.editEmployeeData.state || ""
                    this.address = this.editEmployeeData.address || ""
                    this.emergencyContactName = this.editEmployeeData.emergencyContactName || ""
                    this.emergencyContactEmail = this.editEmployeeData.emergencyContactEmail || ""
                    this.emergencyContactPhone = this.editEmployeeData.emergencyContactPhone || ""
                    this.uanNumber = this.editEmployeeData.uanNumber || ""
                    this.dob = this.editEmployeeData.dob || ""
                    this.companyId = this.editEmployeeData.companyId || ""
                    this.departmentId = this.editEmployeeData.departmentId || ""
                    this.designationId = this.editEmployeeData.designationId || ""
                    this.employmentStatusId = this.editEmployeeData.employmentStatusId || ""
                    this.workLocationId = this.editEmployeeData.workLocationId || ""
                    this.aadhaarCardId = this.editEmployeeData.aadhaarCardId || ""
                    this.panCardId = this.editEmployeeData.panCardId || ""
                    this.passportId = this.editEmployeeData.passportId || ""
                    this.drivingLicenseId = this.editEmployeeData.drivingLicenseId || ""
                    this.localIdProofId = this.editEmployeeData.localIdProofId || ""
                    this.bankAccountName = this.editEmployeeData.bankAccountName || ""
                    this.bankAccountNumber = this.editEmployeeData.bankAccountNumber || ""
                    this.bankAccountBankName = this.editEmployeeData.bankAccountBankName || ""
                    this.bankAccountBranch = this.editEmployeeData.bankAccountBranch || ""
                    this.bankAccountIfsc = this.editEmployeeData.bankAccountIfsc || ""
                    this.bankAccountSwiftId = this.editEmployeeData.bankAccountSwiftId || ""

                    this.profileImageOld = this.editEmployeeData.profileImage || ""
                    this.aadhaarCardUrlOld = this.editEmployeeData.aadhaarCardUrl || ""
                    this.panCardUrlOld = this.editEmployeeData.panCardUrl || ""
                    this.passportUrlOld = this.editEmployeeData.passportUrl || ""
                    this.drivingLicenseUrlOld = this.editEmployeeData.drivingLicenseUrl || ""
                    this.localIdProofUrlOld = this.editEmployeeData.localIdProofUrl || ""
                }
            } else {
                this.editMode = false
            }
            this.userInitialized = true
        },
        async createOrUpdateEmployee() {
            if (this.processing) return
            if (!this.username || this.username.includes(" ")) {
                return this.$toast.error("Please fillup username without spaces")
            }
            if (!this.roleId) {
                return this.$toast.error("User Role is required!")
            }
            if (!this.empCode) {
                return this.$toast.error("Employee Code is required!")
            }

            if (this.password) {
                const err = getPasswordError(this.password)
                if (err) return this.$toast.error(err)
            }

            if (!this.firstName || !this.lastName || !this.email || !this.phone) {
                return this.$toast.error("Please fillup first name, last name, email and phone number")
            }

            if (!this.emergencyContactName || !this.emergencyContactEmail || !this.emergencyContactPhone) {
                return this.$toast.error("Emergency contact details are needed.")
            }
            if (!onlyAlphabetic(this.emergencyContactName)) {
                return this.$toast.error("Emergency contact name can only contain alphabets or space")
            }

            if (!this.country || !this.city || !this.address || !this.state) {
                return this.$toast.error("Country, State, City, and address are mandatory!")
            }

            if (!this.companyId || !this.departmentId || !this.designationId || !this.employmentStatusId || !this.workLocationId) {
                return this.$toast.error("Please fillup mandatory company fields!")
            }

            if (!this.documentDetailsCompleted) {
                return this.$toast.error(
                    "Please fillup mandatory fields in document details!"
                    // "Please fillup mandatory fields in document (including images) and bank details!"
                )
            }

            this.processing = true
            const formDataObj = {
                salutation: this.salutation,
                username: this.username,
                empCode: this.empCode,
                password: this.password,
                fatherName: this.fatherName,
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                phone: this.phone,
                joiningDate: this.joiningDate,
                profileImage: this.profileImage,
                country: this.country,
                city: this.city,
                state: this.state,
                address: this.address,
                emergencyContactName: this.emergencyContactName,
                emergencyContactEmail: this.emergencyContactEmail,
                emergencyContactPhone: this.emergencyContactPhone,
                dob: this.dob,
                // masters
                companyId: this.companyId,
                relatedCompanyIds: this.relatedCompanyIds,
                departmentId: this.departmentId,
                designationId: this.designationId,
                employmentStatusId: this.employmentStatusId,
                workLocationId: this.workLocationId,
                supervisorUserId: this.supervisorUserId,
                uanNumber: this.uanNumber,
                bankMasterId: this.bankMasterId,
                // documents
                aadhaarCardId: this.aadhaarCardId,
                aadhaarCardUrl: this.aadhaarCardUrl,
                panCardId: this.panCardId,
                panCardUrl: this.panCardUrl,
                passportId: this.passportId,
                passportUrl: this.passportUrl,
                drivingLicenseId: this.drivingLicenseId,
                drivingLicenseUrl: this.drivingLicenseUrl,
                localIdProofId: this.localIdProofId,
                localIdProofUrl: this.localIdProofUrl,
                // back account details
                bankAccountName: this.bankAccountName,
                bankAccountNumber: this.bankAccountNumber,
                bankAccountBankName: this.bankAccountBankName,
                bankAccountBranch: this.bankAccountBranch,
                bankAccountIfsc: this.bankAccountIfsc,
                bankAccountSwiftId: this.bankAccountSwiftId,
                // temporarily send for now
                empCode: this.empCode,
                password: this.password,
                roleId: this.roleId,
                // employee role id shoud not change
            }
            const formData = new FormData()
            for (let key in formDataObj) {
                formData.append(key, formDataObj[key])
            }
            try {
                if (this.editMode) {
                    await http.patch(`/employees/${this.editEmployeeData.id}`, formData)
                } else {
                    await http.post("/employees", formData)
                }
                this.$toast.success(this.editMode ? "Employee Updated Successfully" : "Employee Created Successfully")
                if (!this.editMode) {
                    window.location.href = "/employees"
                } else {
                    window.location.href = this.$route.path + "?active_tab=WORK"
                }
            } catch (err) {
                console.log("error in creating employee", err)
                this.$toast.error((err.response && err.response.data && err.response.data.message) || "Some error occured")
            }
            this.processing = false
        },
        async fetchEmployeeCreationMasters() {
            const res = await http.get("/employees/masters")
            const data = res.data.result
            this.companyMastersFull = data.companyMastersFull
            this.userRoles = data.userRoles
            this.companyMasters = data.companyMasters
            this.departmentMasters = data.departmentMasters
            this.designationMasters = data.designationMasters
            this.employmentStatusMasters = data.employmentStatusMasters
            this.workLocationMasters = (data.workLocationMasters || []).map(item => ({
                ...item,
                title: item.title || item.name || ""
            }))
            this.mastersInitialized = true
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.ee-page { padding:24px; max-width:1100px; margin:0 auto; overflow:hidden; }

/* Header */
.ee-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:16px; gap:12px; flex-wrap:wrap; }
.ee-header-left { display:flex; align-items:center; gap:12px; }
.ee-back-btn { width:36px; height:36px; border-radius:$radiusFull; border:1px solid $gray200; background:white; color:$gray500; display:flex; align-items:center; justify-content:center; font-size:13px; transition:all $transitionFast; text-decoration:none; &:hover { background:$gray50; color:$gray700; } }
.ee-title { font-size:20px; font-weight:700; color:$gray800; margin:0; }
.ee-subtitle { font-size:12.5px; color:$gray400; margin:2px 0 0; }
.ee-header-actions { display:flex; gap:8px; flex-wrap:wrap; }
.ee-hbtn { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border-radius:8px; font-size:12px; font-weight:600; cursor:pointer; border:none; font-family:inherit; transition:all $transitionFast; }
.ee-hbtn-email { background:rgba(59,130,246,.1); color:#2563eb; &:hover { background:rgba(59,130,246,.18); } }
.ee-hbtn-dark { background:$gray800; color:white; &:hover { background:$gray700; } }
.ee-hbtn-success { background:rgba(16,185,129,.1); color:#059669; &:hover { background:rgba(16,185,129,.18); } }
.ee-hbtn-danger { background:rgba(239,68,68,.1); color:#dc2626; &:hover { background:rgba(239,68,68,.18); } }

/* Alerts */
.ee-alert { display:flex; align-items:center; gap:8px; padding:8px 14px; border-radius:8px; font-size:12.5px; font-weight:500; margin-bottom:10px; i { font-size:12px; } }
.ee-alert-warn { background:rgba(245,158,11,.08); border:1px solid rgba(245,158,11,.2); color:#b45309; }
.ee-alert-danger { background:rgba(239,68,68,.06); border:1px solid rgba(239,68,68,.15); color:#dc2626; }

/* Tabs */
.ee-tabs { display:flex; gap:4px; margin-bottom:18px; border-bottom:2px solid $gray100; overflow-x:auto; -webkit-overflow-scrolling:touch; }
.ee-tab { display:inline-flex; align-items:center; gap:5px; padding:10px 16px; border:none; background:none; font-size:12.5px; font-weight:500; color:$gray500; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-2px; font-family:inherit; transition:all $transitionFast; border-radius:$radiusSm $radiusSm 0 0; white-space:nowrap; &:hover:not(.ee-tab-active) { color:$gray700; background:$gray50; } }
.ee-tab-active { color:$primaryColor; border-bottom-color:$primaryColor; font-weight:600; background:$primaryLight; }
.ee-tab-icon { font-size:11px; }

/* Sections */
.ee-section { background:white; border:1px solid $gray100; border-radius:10px; padding:20px; margin-bottom:14px; box-shadow:$shadowSm; overflow:visible; }
.ee-section-head { font-size:14px; font-weight:700; color:$gray800; margin-bottom:14px; display:flex; align-items:center; gap:8px; padding-bottom:10px; border-bottom:1px solid $gray100; }
.ee-section-icon { color:$primaryColor; font-size:13px; }

/* Grid / Fields */
.ee-grid { display:grid; grid-template-columns:repeat(2, 1fr); gap:4px 20px; }
.ee-field { min-width:0; overflow:visible; position:relative; }
.ee-field:focus-within { z-index: 100; }
.ee-field-full { grid-column:1 / -1; min-width:0; }
.ee-label { font-size:12.5px; font-weight:600; color:$gray600; margin-bottom:4px; display:block; }

/* Modern form input overrides */
.ee-section ::v-deep .form-group { margin-bottom:2px; }
.ee-section ::v-deep .form-group label { font-size:12.5px; font-weight:600; color:$gray600; }
.ee-section ::v-deep .form-control { border:1px solid $gray200; border-radius:8px; padding:9px 12px; font-size:13px; font-weight:500; color:$gray800; background:white; transition:all $transitionFast; box-shadow:none; height:auto; &:focus { border-color:$primaryColor; box-shadow:0 0 0 3px rgba(0,94,137,.08); outline:none; } &:disabled { background:$gray50; color:$gray400; } }
.ee-section ::v-deep .form-control-sm { font-size:13px; padding:9px 12px; }

/* Doc Grid (Work tab) */
.ee-doc-grid { display:flex; flex-direction:column; gap:0; }
.ee-doc-row { display:grid; grid-template-columns:180px 1fr 1fr; gap:16px; align-items:center; padding:12px 0; border-bottom:1px solid $gray50; &:last-child { border-bottom:none; } }
.ee-doc-label { font-size:13px; font-weight:600; color:$gray700; display:flex; align-items:center; gap:8px; i { color:$primaryColor; font-size:12px; width:18px; text-align:center; } }
.ee-doc-fields { }

/* Face Auth */
.ee-face-auth { display:flex; gap:16px; align-items:flex-start; flex-wrap:wrap; }
.ee-face-img { width:160px; height:160px; object-fit:cover; border-radius:10px; border:2px solid $gray100; }
.ee-face-actions { display:flex; flex-direction:column; gap:6px; }
.ee-face-toggle { display:flex; align-items:center; gap:8px; font-size:13px; }
.ee-empty-sm { padding:16px; text-align:center; color:$gray400; font-size:13px; i { margin-right:6px; } }

/* Buttons */
.ee-actions { display:flex; justify-content:flex-end; gap:8px; padding:14px 0; flex-wrap:wrap; }
.ee-btn { display:inline-flex; align-items:center; gap:6px; padding:9px 18px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; border:none; font-family:inherit; transition:all $transitionFast; }
.ee-btn-primary { background:$primaryColor; color:white; &:hover { background:$primaryDark; } &:disabled { opacity:.6; cursor:not-allowed; } }
.ee-btn-outline { background:white; color:$gray600; border:1px solid $gray200; &:hover { background:$gray50; } }
.ee-btn-red { color:#dc2626; border-color:rgba(239,68,68,.2); &:hover { background:rgba(239,68,68,.05); } }

/* Modal */
.ee-modal-body { padding:4px 0; }
.ee-modal-label { font-size:13px; font-weight:600; color:$gray700; margin-bottom:6px; }

/* Loading */
.ee-loading { display:flex; align-items:center; justify-content:center; gap:8px; padding:60px 20px; font-size:14px; color:$gray400; i { font-size:18px; color:$primaryColor; } }

@media(max-width:768px) {
    .ee-page { padding:16px; }
    .ee-header { flex-direction:column; }
    .ee-grid { grid-template-columns:1fr; }
    .ee-doc-row { grid-template-columns:1fr; gap:6px; }
    .ee-actions { flex-direction:column; }
    .ee-btn { justify-content:center; }
    .ee-tabs { gap:2px; }
    .ee-tab { padding:8px 10px; font-size:11.5px; }
}
</style>
